import { List } from 'immutable'

export const MEDIA_GALLERY_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024

export const MEDIA_OBJECT_ROLE_IDS = [1, 2, 3]

export const getOtherRoleIds = currentRoleId => (
  MEDIA_OBJECT_ROLE_IDS.filter(roleId => roleId !== currentRoleId)
)

export const getMediaObjectRoleId = mediaObject => Number(mediaObject.get('mediaObjectRoleId'))

export const getMediaObjectSorting = mediaObject => Number(mediaObject.get('sorting') ?? 0)

export const sortMediaObjectsBySorting = mediaObjects => (
  mediaObjects.sortBy(getMediaObjectSorting)
)

export const groupMediaObjectsByRole = mediaObjects => (
  MEDIA_OBJECT_ROLE_IDS.reduce((grouped, roleId) => ({
    ...grouped,
    [roleId]: sortMediaObjectsBySorting(
      mediaObjects.filter(mediaObject => getMediaObjectRoleId(mediaObject) === roleId)
    )
  }), {})
)

export const flattenGroupedMediaObjects = grouped => MEDIA_OBJECT_ROLE_IDS.reduce(
  (mediaObjects, roleId) => {
    const roleItems = grouped[roleId].map((mediaObject, index) => mediaObject
      .set('mediaObjectRoleId', roleId)
      .set('sorting', mediaObjects.size + index))

    return mediaObjects.concat(roleItems)
  },
  List()
)

export const isPendingMediaObject = mediaObject => (
  Boolean(mediaObject.get('storedFileId')) && !mediaObject.get('id')
)

export const getMediaObjectKey = mediaObject => (
  mediaObject.get('id') || mediaObject.get('clientId')
)

export const updateMediaObjectField = (mediaObjects, mediaObjectKey, field, value) => {
  const key = String(mediaObjectKey)

  return mediaObjects.map(mediaObject => (
    String(getMediaObjectKey(mediaObject)) === key
      ? mediaObject.set(field, value)
      : mediaObject
  ))
}

export const appendMediaObjectToPrimary = (mediaObjects, mediaObject) => {
  const grouped = groupMediaObjectsByRole(mediaObjects)
  const primaryList = grouped[1].push(mediaObject.set('mediaObjectRoleId', 1))

  return flattenGroupedMediaObjects({
    ...grouped,
    1: primaryList
  })
}

export const splitMediaObjectsForSave = mediaObjects => {
  const pending = mediaObjects.filter(isPendingMediaObject)
  const existing = mediaObjects.filter(mediaObject => !isPendingMediaObject(mediaObject))

  return { pending, existing }
}

const optionalString = value => (value == null ? '' : `${value}`)

const toInteger = value => {
  const parsed = Number(value)

  return Number.isFinite(parsed) ? parsed : null
}

export const buildCreateNewsMediaObjectsPayload = mediaObjects => {
  const { pending } = splitMediaObjectsForSave(mediaObjects)

  return pending.map(mediaObject => ({
    alt_text: optionalString(mediaObject.get('altText')),
    caption: optionalString(mediaObject.get('caption')),
    copyright_info: optionalString(mediaObject.get('copyrightInfo')),
    media_object_role_id: toInteger(mediaObject.get('mediaObjectRoleId')),
    mime_type_id: toInteger(mediaObject.get('mimeTypeId')),
    sorting: toInteger(mediaObject.get('sorting') ?? 0),
    stored_file_id: mediaObject.get('storedFileId') || undefined
  }))
    .filter(item => item.media_object_role_id != null && item.mime_type_id != null && item.sorting != null && item.stored_file_id)
    .toJS()
}

export const buildUpdateNewsMediaObjectsPayload = mediaObjects => {
  const { existing } = splitMediaObjectsForSave(mediaObjects)

  return existing.map(mediaObject => ({
    alt_text: optionalString(mediaObject.get('altText')),
    caption: optionalString(mediaObject.get('caption')),
    copyright_info: optionalString(mediaObject.get('copyrightInfo')),
    id: toInteger(mediaObject.get('id')),
    media_object_role_id: toInteger(mediaObject.get('mediaObjectRoleId')),
    sorting: toInteger(mediaObject.get('sorting') ?? 0)
  }))
    .filter(item => item.id != null && item.media_object_role_id != null && item.sorting != null)
    .toJS()
}

const getImageComparableUrl = image => (
  image.get('originalUrl') || image.get('url')
)

export const syncImagesRelevantWithPrimary = (images, mediaObjects) => {
  if (!images?.size || !mediaObjects?.size) {
    return images
  }

  const primaryFirstUrl = groupMediaObjectsByRole(mediaObjects)[1].get(0)?.get('url')

  if (!primaryFirstUrl) {
    return images
  }

  const relevantIndex = images.findIndex(image => getImageComparableUrl(image) === primaryFirstUrl)

  if (relevantIndex < 0) {
    return images
  }

  return images.map((image, index) => image.set('relevant', index === relevantIndex))
}
